import React, { useCallback } from 'react'
import Group from 'components/utils/Group'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Image from 'components/Common/Image'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import styled from 'styled-components'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replaceSearchString } from 'actions/NavigationActions'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import { track } from 'analytics/analytics'
import Spacing from 'components/Common/Spacing'
import { rem } from 'polished'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import { clientEvent } from 'analytics/snowplow/events'
import { clickBroadSearchLocation } from 'analytics/optimizely/events'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const LOCATIONS = {
  le_64930b7151b6bc3e289f37d5da62ac9f: 'bali',
  le_d3d9446802a44259755d38e6d163e820: 'australia',
  le_8f85517967795eeef66c225f7883bdcb: 'thailand',
  le_12767faf1bf8cafc7594430586c558c2: 'europe',
}

const DESTINATIONS = {
  le_64930b7151b6bc3e289f37d5da62ac9f: [
    {
      name: 'Ubud',
      caption: 'Serene, spiritual, tranquil',
      image: 'broad-search-ubud',
      id: 'le_7f7ed8ecfca9e17696ff654508efd86a',
    },
    {
      name: 'Seminyak',
      caption: 'Beachfront, trendy, bustling',
      image: 'broad-search-seminyak',
      id: 'le_790d9f1f52e10ef90ede8efcf7f3c078',
    },
    {
      name: 'Nusa Dua',
      caption: 'Luxurious, secluded, resorts',
      image: 'broad-search-nusa-dua',
      id: 'le_a55472983efa2e239b315de91576d1d3',
    },
  ],
  le_d3d9446802a44259755d38e6d163e820: [
    {
      name: 'Sydney',
      caption: 'Vibrant, coastal, cultural',
      image: 'broad-search-sydney',
      id: 'le_678c61ac5b4f91ca86e112fc50e63766',
    },
    {
      name: 'Gold Coast, QLD',
      caption: 'Sun, surf, entertainment',
      image: 'broad-search-gold-coast',
      id: 'le_bac732346917b0a1231354ef55faf00a',
    },
    {
      name: 'Perth, WA',
      caption: 'Laid-back, sunny, scenic',
      image: 'broad-search-perth',
      id: 'le_7c27cf9f8fe09724bb754e474610eb0e',
    },
    {
      name: 'Melbourne, VIC',
      caption: 'Dynamic, artsy, cosmopolitan',
      image: 'broad-search-melbourne',
      id: 'le_15fa44474c92da27cf2125373356db4c',
    },
    {
      name: 'Brisbane, QLD',
      caption: 'Riverfront, lush, lively',
      image: 'broad-search-brisbane',
      id: 'le_b96d145f6c31e35755f4a6841574ba37',
    },
    {
      name: 'Adelaide, SA',
      caption: 'Picturesque, cultural, wine centric',
      image: 'broad-search-adelaide',
      id: 'le_f4c2c73d7fe5eb38fd83d32a61604247',
    },
    {
      name: 'Hobert, TAS',
      caption: 'Quaint, historic, charming',
      image: 'broad-search-hobart',
      id: 'le_b573c52d599a979ba8c16e9789bb8014',
    },
    {
      name: 'Cairns, QLD',
      caption: 'Tropical, adventure-filled, reef',
      image: 'broad-search-cairns',
      id: 'le_b7218568a1335addb2966d47e1fe939c',
    },
  ],
  le_8f85517967795eeef66c225f7883bdcb: [
    {
      name: 'Phuket',
      caption: 'Tropical, vibrant, beachy',
      image: 'broad-search-phuket',
      id: 'le_d47bb339212ddfab0fadd99d492a5fc3',
    },
    {
      name: 'Koh Samui',
      caption: 'Exotic, relaxing, island paradise',
      image: 'broad-search-koh-samui',
      id: 'le_7af6d6117d13124fd3e00f5cb5f04400',
    },
    {
      name: 'Bangkok',
      caption: 'Dynamic, bustling, cultural',
      image: 'broad-search-bangkok',
      id: 'le_ff7aa1f9b4062bfee0f99a0453506f1b',
    },
    {
      name: 'Khao Lak',
      caption: 'Serene, coastal, tranquil',
      image: 'broad-search-khao-lak',
      id: 'le_f3ad1c493ba61ad63bb43d6947f42d95',
    },
  ],
  le_12767faf1bf8cafc7594430586c558c2: [
    {
      name: 'Spain',
      caption: 'Vibrant, cultural, diverse',
      image: 'broad-search-spain',
      id: 'le_7e7757b1e12abcb736ab9a754ffb617a',
    },
    {
      name: 'France',
      caption: 'Romantic, chic, gastronomic',
      image: 'broad-search-france',
      id: 'le_093f65e080a295f8076b1c5722a46aa2',
    },
    {
      name: 'Italy',
      caption: 'Rich, cultural, culinary',
      image: 'broad-search-italy',
      id: 'le_93db85ed909c13838ff95ccfa94cebd9',
    },
    {
      name: 'England',
      caption: 'Dynamic, bustling, cultural',
      image: 'broad-search-england',
      id: 'le_568221292bd68ebdd821521ce60a8110',
    },
    {
      name: 'Greece',
      caption: 'Serene, coastal, tranquil',
      image: 'broad-search-greece',
      id: 'le_735b90b4568125ed6c3f678819b6e058',
    },
    {
      name: 'Portugal',
      caption: 'Charming, coastal, picturesque',
      image: 'broad-search-portugal',
      id: 'le_8d5e957f297893487bd98fa830fa6413',
    },
    {
      name: 'Germany',
      caption: 'Cultural diverse, tranquil',
      image: 'broad-search-germany',
      id: 'le_ea5d2f1c4608232e07d3aa3d998e5135',
    },
  ],
}

const DestinationImage = styled(Image)`
  width: 220px;
  height: 80px;
`

const ClickableGroup = styled(Group)`
  cursor: pointer;
  padding: ${rem(8)};

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
      border-radius: ${props => props.theme.borderRadius.S}
    }
  }
`

const APPLICABLE_DESTINATIONS = Object.keys(DESTINATIONS)
interface Props {
  destinationId: string;
  destinationName: string;
}

function HotelBroadSearchLocation({ destinationId, destinationName }: Props) {
  const dispatch = useAppDispatch()

  const onClick = useCallback((id: string, name: string) => {
    track('click-broad-search-location', clickBroadSearchLocation())
    track('client-event', clientEvent({
      subject: `${LOCATIONS[destinationId]}-${name}`,
      action: 'clicked',
      category: 'broad_search_location',
      type: 'interaction',
    }))
    dispatch(replaceSearchString({ destinationId: id, destinationName: name }))
  }, [destinationId, dispatch])

  const showLocations = useOptimizelyExperiment(OptimizelyClientSideExperiments.searchBroadSearchLocation, APPLICABLE_DESTINATIONS.includes(destinationId))

  return (<>
    {showLocations && <Group direction="vertical" gap={8}>
      <BodyTextBlock variant="large">
        Explore top destinations in {destinationName}
      </BodyTextBlock>
      <CardCarousel gap={0}>
        {DESTINATIONS[destinationId].map((destination) => (
          <ClickableGroup onClick={() => onClick(destination.id, destination.name)} direction="vertical" gap={8} key={destination.id}>
            <DestinationImage id={destination.image} alt={destination.name}/>
            <Group direction="vertical">
              <BodyText variant="medium" weight="bold">{destination.name}</BodyText>
              <Caption colour="neutral-three" variant="medium">{destination.caption}</Caption>
            </Group>
          </ClickableGroup>
        ))}
      </CardCarousel>
      <Spacing pt={8} ptSm={16} />
    </Group>}
  </>)
}

export default HotelBroadSearchLocation
